export type Locale = "en" | "ar";

export const LOCALE_COOKIE = "ticketing_locale";

export const MESSAGES: Record<Locale, Record<string, string>> = {
  en: {
    // Nav
    "nav.dashboard": "Home",
    "nav.tickets": "Tickets",
    "nav.newTicket": "+New Ticket",
    "nav.users": "Users",
    "nav.catalog": "Catalog",
    "nav.admin": "Admin",
    "nav.logout": "Logout",
    "nav.language": "Language",
    "nav.routine": "Routine",
    "nav.routineInspections": "View Inspection",
    "nav.assets": "Assets",
    "nav.menu": "Menu",
    "nav.openMenu": "Open menu",
    "nav.closeMenu": "Close menu",

    // Asset management
    "assets.title": "Asset management",
    "assets.subtitle": "Register and maintain equipment used in routine inspections.",
    "assets.registry.title": "Asset registry",
    "assets.settings.title": "Asset settings",
    "assets.settings.subtitle": "Configure locations, statuses, and categories used when registering assets.",
    "assets.settings.tab.categories": "Categories",
    "assets.settings.tab.locations": "Locations",
    "assets.settings.tab.status": "Status",
    "assets.settings.addLocation": "Add location",
    "assets.settings.addStatus": "Add status",
    "assets.settings.locationName": "Location name",
    "assets.settings.statusName": "Status name",
    "assets.settings.operational": "Counts as active / in service",
    "assets.settings.operationalYes": "In service",
    "assets.settings.operationalNo": "Out of service",
    "assets.settings.list.categories": "Asset categories",
    "assets.settings.list.locations": "Asset locations",
    "assets.settings.list.status": "Asset statuses",
    "assets.settings.needLocation": "Add locations under Asset settings",
    "assets.categories.title": "Asset categories",
    "assets.categories.subtitle": "Group assets by type or location for scheduling and reporting.",
    "assets.back": "Back to asset registry",
    "assets.manageSettingsHint": "Configure options under",
    "assets.stats.total": "Total assets",
    "assets.stats.active": "Active",
    "assets.stats.inactive": "Inactive",

    // Routine inspections
    "routine.dashboard.title": "Routine inspections",
    "routine.dashboard.subtitle": "Plan, schedule, and track periodic equipment inspections.",
    "routine.dashboard.dueSoon": "Due soon (7d)",
    "routine.dashboard.overdue": "Overdue",
    "routine.dashboard.inProgress": "In progress",
    "routine.dashboard.completedMonth": "Completed this month",
    "routine.assets.title": "Assets",
    "routine.categories.title": "Asset categories",
    "routine.checklists.title": "Checklist templates",
    "routine.checklists.viewItems": "View items",
    "routine.checklists.editItems": "Edit items",
    "routine.checklists.saveDraft": "Save draft",
    "routine.checklists.publish": "Publish",
    "routine.checklists.publishedSuccess": "Checklist published.",
    "routine.checklists.draftBadge": "Draft",
    "routine.checklists.publishedBadge": "Published",
    "routine.checklists.version": "Version {n}",
    "routine.checklists.back": "Back to checklists",
    "routine.checklists.editingDraft": "This checklist has unpublished draft changes.",
    "routine.checklists.publishedReadOnly": "Published checklist. Click Edit items to change the steps.",
    "routine.schedules.title": "Add New Inspection",
    "routine.schedules.subtitle": "Define how often each asset should be inspected and who is responsible.",
    "routine.inspections.title": "View Inspection",
    "routine.reports.title": "Inspection reports",
    "routine.reports.subtitle": "View completed inspections with full details, print, or export.",
    "routine.reports.detailTitle": "Inspection report",
    "routine.reports.back": "Back to reports",
    "routine.reports.print": "Print",
    "routine.reports.exportCsv": "Export CSV",
    "routine.reports.summary": "Summary",
    "routine.reports.overallResult": "Overall result",
    "routine.reports.overallNotes": "Overall notes",
    "routine.reports.building": "Building",
    "routine.reports.checklist": "Checklist",
    "routine.reports.scheduledAt": "Scheduled",
    "routine.reports.startedAt": "Started",
    "routine.reports.submittedAt": "Submitted",
    "routine.reports.completedAt": "Completed",
    "routine.reports.failedItems": "Failed items",
    "routine.reports.correctiveTicket": "Corrective ticket",
    "routine.reports.checklistResults": "Checklist results",
    "routine.reports.item": "Item",
    "routine.reports.response": "Response",
    "routine.reports.notes": "Notes",
    "routine.reports.result": "Result",
    "routine.reports.passed": "Passed",
    "routine.reports.failed": "Failed",
    "routine.reports.progressHistory": "Progress history",
    "routine.reports.images": "Images",
    "routine.empty": "No records yet.",
    "routine.view": "View",
    "routine.status": "Status",
    "routine.due": "Due",
    "routine.nextDue": "Next due",
    "routine.asset": "Asset",
    "routine.back": "Back to inspections",
    "routine.create.new": "New",
    "routine.create.save": "Save",
    "routine.create.saving": "Saving…",
    "routine.create.error": "Could not save.",
    "routine.create.categoryTitle": "Add category",
    "routine.create.categoryName": "Name",
    "routine.create.categoryDesc": "Description",
    "routine.create.assetTitle": "Add asset",
    "routine.create.assetCode": "Asset code",
    "routine.create.assetName": "Name",
    "routine.create.assetCategory": "Category",
    "routine.create.assetLocation": "Location",
    "routine.create.needCategory": "Create an asset category first.",
    "routine.create.checklistTitle": "Add checklist template",
    "routine.create.checklistName": "Checklist name",
    "routine.create.checklistItems": "Checklist items",
    "routine.create.checklistItemsHint": "Add every step technicians should verify during the inspection.",
    "routine.create.checklistItem": "Item title",
    "routine.create.addItem": "Add item",
    "routine.create.removeItem": "Remove",
    "routine.create.itemsRequired": "Add at least one checklist item.",
    "routine.create.connectInspection": "Connect to inspection",
    "routine.create.linkAsset": "Asset",
    "routine.create.linkAssetHint": "Optionally link this checklist to an asset to schedule recurring inspections. You can also connect it later from Inspection schedules.",
    "routine.create.noAssetLink": "Do not link yet",
    "routine.create.checklistItemCount": "{count} items",
    "routine.create.defaultItem": "General inspection",
    "routine.create.scheduleTitle": "Add inspection schedule",
    "routine.create.scheduleNeedPrereq": "Create at least one asset and one published checklist first.",
    "routine.create.scheduleAsset": "Asset",
    "routine.create.scheduleChecklist": "Checklist version",
    "routine.create.scheduleFrequency": "Frequency",
    "routine.create.scheduleStart": "Start date",
    "routine.create.scheduleAssignee": "Assign to technician",
    "routine.create.unassigned": "Unassigned",
    "routine.create.pick": "Select…",
    "routine.create.required": "Please fill all required fields.",
    "routine.create.generateHint": "After saving a schedule, generate inspection tasks for all due schedules.",
    "routine.create.generateButton": "Generate due inspections",
    "routine.create.generateRunning": "Generating…",
    "routine.create.generateSuccess": "Created {count} inspection task(s).",
    "routine.create.generateError": "Generation failed.",
    "routine.setup.title": "Set up routine inspections",
    "routine.setup.step1": "1. Add asset categories",
    "routine.setup.step2": "2. Register assets",
    "routine.setup.step3": "3. Create checklist templates",
    "routine.setup.step4": "4. Configure schedules & generate tasks",
    "routine.edit": "Edit",
    "routine.delete": "Delete",
    "routine.cancel": "Cancel",
    "routine.cancelConfirm": "Cancel this inspection?",
    "routine.deleteConfirm": "Delete this item?",
    "routine.active": "Active",
    "routine.inactive": "Inactive",
    "routine.yes": "Yes",
    "routine.no": "No",
    "routine.assign": "Assign",
    "routine.assignee": "Technician",
    "routine.assignPick": "Select technician",
    "routine.confirmDelete": "Yes, delete",
    "routine.cancelAction": "Cancel",
    "routine.savedSuccess": "Saved successfully.",
    "routine.deletedSuccess": "Deleted successfully.",
    "routine.deactivatedSuccess": "Deactivated (linked inspections exist).",
    "routine.cancelledSuccess": "Inspection cancelled.",
    "routine.confirmCancel": "Yes, cancel",

    // Users
    "users.title": "Users",
    "users.col.id": "ID",
    "users.col.employeeId": "Employee ID",
    "users.col.employeeIdHint": "Parsed from name when formatted as 211002 - Full Name",
    "users.col.name": "Name in English",
    "users.col.nameEn": "Name in Arabic",
    "users.col.mobile": "Mobile",
    "users.col.email": "Email",
    "users.col.telegram": "Telegram ID",
    "users.col.created": "Created",
    "users.empty": "No users yet.",
    "users.searchPlaceholder": "Search by name, mobile, or email…",
    "users.noResults": "No users match your search.",
    "users.edit": "Edit",
    "users.editTitle": "Edit user",
    "users.save": "Save",
    "users.cancel": "Cancel",
    "users.saving": "Saving…",
    "users.errorSave": "Could not save changes.",
    "users.addNew": "Add user",
    "users.addTitle": "Add new user",
    "users.errorCreate": "Could not create user.",
    "users.needOneField":
      "Enter at least one of: employee ID, name (English/Arabic), mobile, or email.",
    "users.employeeIdExists": "User already exists",
    "users.delete": "Delete",
    "users.deleteConfirm": "Delete this user? This cannot be undone.",
    "users.deleting": "Deleting…",
    "users.errorDelete": "Could not delete user.",
    "users.syncHr": "Sync from HR",
    "users.syncingHr": "Syncing from HR…",
    "users.syncHrDone": "HR sync complete: {inserted} added, {updated} updated ({fetched} from HR).",
    "users.syncHrFailed": "Could not sync users from HR.",

    // Catalog setup
    "catalog.title": "Catalog Setup",
    "catalog.company": "Company",
    "catalog.branch": "Branch",
    "catalog.department": "Department",
    "catalog.requestCategory": "Request Category",
    "catalog.subcategory": "Subcategory",
    "catalog.nameEn": "Name (English)",
    "catalog.nameAr": "Name (Arabic)",
    "catalog.add": "Add",
    "catalog.saving": "Saving...",
    "catalog.deleting": "Deleting...",
    "catalog.edit": "Edit",
    "catalog.delete": "Delete",
    "catalog.search": "Search in this level...",
    "catalog.confirmDelete": "Delete this record?",
    "catalog.empty": "No records yet.",
    "catalog.selectCompany": "Select company",
    "catalog.selectBranch": "Select branch",
    "catalog.selectDepartment": "Select department",
    "catalog.selectRequestCategory": "Select request category",

    // Dashboard
    "home.hero.pill": "Fast • Organized • Connected",
    "home.hero.title":
      "Alamer Ticketing System - Maintenance Department",
    "home.hero.subtitle":
      "Create tickets, follow them up, and close them promptly.",
    "home.hero.ctaPrimary": "Create Ticket Now",
    "home.hero.ctaSecondary": "View All Tickets",
    "home.actions.create.title": "Create Ticket",
    "home.actions.create.body":
      "Log a new request within seconds and keep the support queue organized.",
    "home.actions.track.title": "Track Status",
    "home.actions.track.body":
      "Quickly know what is open, in progress, resolved, or closed.",
    "home.actions.telegram.title": "Telegram Bot",
    "home.actions.telegram.body":
      "Allow users to create and track tickets from Telegram. Fast support from mobile.",

    "dashboard.title": "Ticket overview",
    "dashboard.subtitle": "Live summary of maintenance ticket activity.",
    "dashboard.total": "Total tickets",
    "dashboard.new": "New",
    "dashboard.inProgress": "In progress",
    "dashboard.pending": "Pending",
    "dashboard.closed": "Closed",
    "dashboard.reopened": "Reopened",
    "dashboard.chartStatus": "Tickets by status",
    "dashboard.chartTechnicians": "Assigned per technician",
    "dashboard.chartTrend": "New tickets (last 7 days)",
    "dashboard.unassigned": "Unassigned tickets",
    "dashboard.noData": "No ticket data yet.",
    "dashboard.status.OPEN": "New / Open",
    "dashboard.status.NEW": "New",
    "dashboard.status.ASSIGNED": "Assigned",
    "dashboard.status.IN_PROGRESS": "In progress",
    "dashboard.status.PENDING": "Pending",
    "dashboard.status.RESOLVED": "Resolved",
    "dashboard.status.CLOSED": "Closed",
    "dashboard.status.REOPENED": "Reopened",
    "dashboard.filterAssignee": "Assignee",
    "dashboard.filterStatus": "Ticket status",
    "dashboard.allAssignees": "All assignees",
    "dashboard.allStatuses": "All statuses",
    "dashboard.ticketsLabel": "tickets",
    "dashboard.showing": "Showing",
    "dashboard.noFilterResults": "No tickets match these filters.",

    // Telegram setup
    "telegramSetup.title": "Connect the Telegram bot",
    "telegramSetup.intro":
      "Follow these steps so users can create and track tickets from Telegram.",
    "telegramSetup.warning":
      "This page shows your bot token. Anyone with access to this URL can see it. Protect the app with authentication or a private network before exposing it on the internet.",
    "telegramSetup.step1Title": "1. Create a bot with BotFather",
    "telegramSetup.step1Body":
      "In Telegram, open @BotFather, send /newbot, choose a display name and username, then copy the HTTP API token.",
    "telegramSetup.step2Title": "2. Save the token in environment variables",
    "telegramSetup.step2Body":
      "In the web folder, open .env and set TELEGRAM_BOT_TOKEN to your token (no quotes). Restart the dev server afterward.",
    "telegramSetup.step3Title": "3. Use HTTPS for the webhook URL",
    "telegramSetup.step3Body":
      "Telegram requires an https:// webhook URL in production. For local development, use a tunnel (for example ngrok) and use the https URL as your public base.",
    "telegramSetup.step4Title": "4. Register the webhook",
    "telegramSetup.step4Body":
      "Call setWebhook once with your public base URL plus the path below. You can use curl or open the URL in a browser.",
    "telegramSetup.webhookLabel": "Webhook endpoint (this app)",
    "telegramSetup.exampleCurl": "Example curl",
    "telegramSetup.tokenSection": "Current bot token (from server)",
    "telegramSetup.tokenMissing":
      "No token is configured. Set TELEGRAM_BOT_TOKEN in web/.env and restart the server.",
    "telegramSetup.tokenPlaceholder":
      "A placeholder is set in .env. Replace TELEGRAM_BOT_TOKEN with the real token from BotFather.",
    "telegramSetup.copy": "Copy",
    "telegramSetup.copied": "Copied!",

    // Telegram bot (FSM)
    "bot.lang.title": "Choose your language - أختر اللغة",
    "bot.phone.request": "Share your phone number to continue.",
    "bot.phone.button": "Share phone",
    "bot.phone.saved": "Phone saved!",
    "bot.phone.notRegistered":
      "Your number is not registered. Please contact the administrator.",
    "bot.phone.mismatch":
      "This phone does not match your registered profile. Please share your own registered number.",
    "bot.menu.title": "Main menu",
    "bot.menu.create": "Create new ticket",
    "bot.menu.view": "View my tickets",
    "bot.menu.changeDepartment": "Change department",
    "bot.menu.changeLang": "Change language",
    "bot.menu.routineInspections": "Routine inspections",
    "bot.menu.assigned": "Tickets assigned to me",
    "bot.assigned.title": "Tickets assigned to you",
    "bot.assigned.empty": "No tickets are assigned to you right now.",
    "bot.assigned.details": "Assigned ticket",
    "bot.assigned.updateStatus": "Update status",
    "bot.assigned.askImages":
      "Ticket #{id}: send images (optional, up to 5), then tap Next — or Skip.",
    "bot.assigned.askCommentStep":
      "Add a comment (optional), then tap Next — or Skip.",
    "bot.assigned.askStatus": "Choose the new status, or Cancel to return to the main menu.",
    "bot.assigned.next": "Next",
    "bot.assigned.skip": "Skip",
    "bot.assigned.cancel": "Cancel",
    "bot.assigned.cancelled": "Update cancelled. Back to main menu.",
    "bot.assigned.imageAdded":
      "Image added ({count}/{max}). Send more, or tap Next / Skip.",
    "bot.assigned.imagesFull": "Maximum of 5 images reached. Tap Next to continue.",
    "bot.assigned.imageOnly": "Images only are allowed in this step.",
    "bot.assigned.commentSaved": "Comment saved. Now choose a status.",
    "bot.assigned.defaultComment": "Update",
    "bot.assigned.statusUpdated": "Status updated to: {status}",
    "bot.assigned.updatedDone": "Ticket #{id} updated.",
    "bot.assigned.notYours": "This ticket is not assigned to you.",
    "bot.assigned.notEmployee": "This option is only available for employees.",
    "bot.assigned.closeAskResolution": "Send the resolution details to close ticket #{id}.",
    "bot.assigned.closeCancelled": "Close cancelled.",
    "bot.assigned.back": "Back to assigned tickets",
    "bot.assigned.cancelClose": "Cancel",

    "bot.routine.backMenu": "Main menu",
    "bot.routine.backList": "Back to list",
    "bot.routine.updateProgress": "Update progress",
    "bot.routine.submitForReview": "Submit for review",
    "bot.routine.submitted": "Submitted for review.",
    "bot.routine.progressLocked": "Progress is 100%. Submit for review — you cannot change progress anymore.",
    "bot.routine.pctTooLow": "Cannot set progress lower than the current percentage.",
    "bot.routine.noHigherPct": "No higher preset left. Use Custom, or choose Completed status.",
    "bot.routine.updateStatus": "Update status",
    "bot.routine.updatePct": "Update progress %",
    "bot.routine.addNote": "Add note",
    "bot.routine.uploadImage": "Upload images",
    "bot.routine.pickStatus": "Step 1/3 — Choose progress status:",
    "bot.routine.pickPct": "Step 2/3 — Choose progress percentage:",
    "bot.routine.enterProgressNote": "Step 3/3 — Send a note/comment for this update:",
    "bot.routine.enterCustomPct": "Enter a percentage higher than current (max 99):",
    "bot.routine.invalidCustomPct": "Invalid percentage (must be higher than current and below 100).",
    "bot.routine.sendPhotoOrSkip": "Optionally send a photo for this update, or tap Skip.",
    "bot.routine.skipPhoto": "Skip photo",
    "bot.routine.noteRequired": "Please send a note (text message).",
    "bot.routine.enterNote": "Send your note:",
    "bot.routine.sendPhoto": "Send a photo now.",
    "bot.routine.saved": "Saved.",
    "bot.routine.imageSaved": "Image saved.",
    "routine.activity.title": "Activity",
    "routine.activity.empty": "No progress updates yet.",
    "routine.activity.olderImages": "Older images",
    "routine.activity.status": "Status",
    "routine.activity.percent": "Progress",
    "routine.activity.note": "Note",
    "bot.create.ask.skip": "Skip",
    "bot.create.ask.enter": "Enter name",
    "bot.create.enterPrompt": "Send the name (text).",
    "bot.create.descriptionPrompt":
      "Send a short description (min 3 characters).",
    "bot.create.descriptionInvalid":
      "Description is too short. Please write at least 3 characters.",
    "bot.create.attachQuestion": "Do you want to attach a file?",
    "bot.create.attachYes": "Yes, attach a file",
    "bot.create.attachNo": "No attachment",
    "bot.create.attachmentInstruction": "Send a photo or a document (max 10 MB).",
    "bot.create.attachmentInvalid": "Invalid attachment. Send a photo or document.",
    "bot.create.attachmentTooLarge": "Attachment is too large. Max 10 MB.",
    "bot.create.confirmTitle": "Confirm ticket",
    "bot.create.confirmSubject": "Subject",
    "bot.create.confirmDescription": "Description",
    "bot.create.confirmPhone": "Phone",
    "bot.create.confirmDetails": "Details",
    "bot.create.confirmAttachment": "Attachment",
    "bot.create.confirmAttachmentNone": "None",
    "bot.create.confirmYes": "Confirm ✅",
    "bot.create.confirmEdit": "Edit ✏️",
    "bot.create.confirmCancel": "Cancel ❌",
    "bot.create.confirmInProgress": "Please wait… we are already creating your ticket.",
    "bot.create.cancelled": "Cancelled. Back to main menu.",
    "bot.create.sessionExpired":
      "Your session timed out. Ticket draft was reset. Please start again from main menu.",
    "bot.create.created": "Ticket created successfully!",
    "bot.create.ticketId": "Ticket ID: ",
    "bot.create.attachmentFailed": "Ticket created, but attachment upload failed.",
    "bot.view.title": "Your tickets",
    "bot.view.empty": "You have no tickets yet.",
    "bot.view.back": "Back",
    "bot.view.details": "Ticket details",
    "bot.view.detailsAttachment": "Attachment",
    "bot.view.noComments": "No comments yet.",
    "bot.error.general": "Something went wrong. Please try again.",
    "bot.contact.invalid": "Invalid contact. Please use the Share phone button.",
    "bot.create.step.company": "choose Company",
    "bot.create.step.branch": "choose Branch",
    "bot.create.step.site": "choose Site / Location",
    "bot.create.step.department": "choose Department",
    "bot.create.step.category": "choose Category",
    "bot.create.step.subcategory": "choose Subcategory",
    "bot.create.step.item": "choose Item (optional)",

    // Tickets
    "tickets.new.title": "New Ticket",
    "tickets.field.title": "Title",
    "tickets.field.description": "Description",
    "tickets.field.priority": "Priority",
    "tickets.field.company": "Company",
    "tickets.field.companyPlaceholder": "Select company",
    "tickets.field.category": "Category",
    "tickets.field.categoryPlaceholder": "Select category",
    "tickets.field.assignedTo": "Assign to",
    "tickets.field.assignedToNone": "Unassigned",
    "tickets.field.attachment": "Attachment (image)",
    "tickets.field.attachmentHint":
      "Optional. JPEG, PNG, WebP, or GIF. Max 5 MB.",
    "tickets.error.invalidImage": "Please choose a valid image file.",
    "tickets.error.fileTooLarge": "Image must be 5 MB or smaller.",
    "tickets.error.dbColumn":
      "The database your app uses (see .env DATABASE_URL: same host + database= name) still has no attachmentUrl column, or the dev server needs a restart. In SSMS connect to that exact server and database, then run: ALTER TABLE dbo.Ticket ADD attachmentUrl NVARCHAR(500) NULL; Stop npm run dev, run npx prisma generate, start again.",
    "tickets.action.create": "Create Ticket",
    "tickets.action.creating": "Creating...",
    "tickets.error.companyRequired": "Please select a company.",
    "tickets.error.categoryRequired": "Please select a category.",
    "tickets.error.createFailed": "Failed to create ticket",
    "tickets.list.title": "Tickets",
    "tickets.list.new": "New Ticket",
    "tickets.list.empty": "No tickets yet.",
    "tickets.list.emptyFiltered": "No tickets match these filters.",
    "tickets.list.createFirst": "Create the first one",
    "tickets.list.filters": "Ticket filters",
    "tickets.list.filter.search": "Search",
    "tickets.list.filter.searchPlaceholder": "Title or ticket ID",
    "tickets.list.filter.status": "Status",
    "tickets.list.filter.priority": "Priority",
    "tickets.list.filter.owner": "Assignee",
    "tickets.list.filter.allStatuses": "All statuses",
    "tickets.list.filter.allPriorities": "All priorities",
    "tickets.list.filter.allOwners": "All assignees",
    "tickets.list.filter.unassigned": "Unassigned",
    "tickets.list.filter.apply": "Apply",
    "tickets.list.filter.clear": "Clear",
    "tickets.list.showing": "Showing {count} {label}",
    "tickets.list.ticketsLabel": "tickets",
    "tickets.list.col.id": "ID",
    "tickets.list.col.title": "Title",
    "tickets.list.col.status": "Status",
    "tickets.list.col.priority": "Priority",
    "tickets.list.col.owner": "Owner",
    "tickets.list.col.requestCategory": "Category",
    "tickets.list.col.created": "Created",
    "tickets.list.page": "Page {page} of {total}",
    "tickets.list.prev": "Previous",
    "tickets.list.next": "Next",

    "login.brandCompany": "Abdulla Ali Alamer & Bros Co.",
    "login.brandTagline": "Maintenance Ticketing System",
    "login.title": "Sign in",
    "login.bootstrapTitle": "Create first admin",
    "login.hint": "Use employee ID and password.",
    "login.employeeId": "Employee ID",
    "login.password": "Password",
    "login.fullName": "Full name",
    "login.submit": "Sign in",
    "login.bootstrapSubmit": "Create admin",
    "login.loading": "Please wait…",

    // Ticket detail labels
    "ticket.status": "Status",
    "ticket.priority": "Priority",
    "ticket.priority.LOW": "Low",
    "ticket.priority.MEDIUM": "Medium",
    "ticket.priority.HIGH": "High",
    "ticket.priority.URGENT": "Urgent",
    "ticket.resolutionDetails": "Resolution details (required to close)",
    "ticket.resolutionPlaceholder": "Describe what was done to resolve the issue…",
    "ticket.closeTicket": "Close ticket",
    "ticket.cancel": "Cancel",
    "ticket.owner": "Owner",
    "ticket.ownerPhone": "Owner phone",
    "ticket.assignedTo": "Assigned to",
    "ticket.assignedPeople": "Assigned people",
    "ticket.assignedPeopleNone": "No auto-assigned people for this category",
    "ticket.unassigned": "Unassigned",
    "ticket.back": "Back to tickets",
    "ticket.details": "Details",
    "ticket.attachment": "Attachment",
    "ticket.downloadAttachment": "Download attachment",
    "ticket.delete": "Delete ticket",
    "ticket.deleting": "Deleting…",
    "ticket.deleteConfirm": "Delete ticket #{id}? This cannot be undone.",
    "ticket.deleteError": "Could not delete ticket.",
    "ticket.resolution": "Resolution",
    "ticket.closed": "Closed",
    "ticket.company": "Company",
    "ticket.branch": "Branch",
    "ticket.department": "Department",
    "ticket.requestCategory": "Request Category",
    "ticket.subcategory": "Subcategory",
    "ticket.created": "Created Date",
    "ticket.updated": "Last Updated",
    "ticket.comments.title": "Comments",
    "ticket.comments.empty": "No comments yet. Add the first note for the team.",
    "ticket.comments.author": "Your name (optional)",
    "ticket.comments.placeholder": "Write an internal note or update for the team…",
    "ticket.comments.post": "Add comment",
    "ticket.comments.posting": "Posting…",
    "ticket.comments.error": "Could not post comment.",
    "ticket.comments.edit": "Edit",
    "ticket.comments.delete": "Delete",
    "ticket.comments.save": "Save",
    "ticket.comments.cancel": "Cancel",
    "ticket.comments.confirmDelete": "Delete this comment?",
    "ticket.comments.updateError": "Could not update comment.",
    "ticket.comments.deleteError": "Could not delete comment.",
    "ticket.comments.attachImages": "Attach images (optional)",
    "ticket.comments.attachHint": "Images only · up to 5 · max 5 MB each",
    "ticket.comments.attachTooMany": "You can attach at most 5 images.",
    "ticket.comments.attachInvalid": "Only image files are allowed.",
    "ticket.comments.removeImage": "Remove image",

    // Admin accounts
    "admin.accounts.pageTitle": "Employee Accounts",
    "admin.accounts.detailTitle": "Employee account",
    "admin.accounts.backToList": "Back to employees",
    "admin.accounts.searchPlaceholder": "Search by name, employee ID, or mobile…",
    "admin.accounts.addNew": "Add employee",
    "admin.accounts.cancel": "Cancel",
    "admin.accounts.empty": "No employee accounts yet.",
    "admin.accounts.noResults": "No employees match your search.",
    "admin.accounts.col.id": "ID",
    "admin.accounts.col.employeeId": "Employee ID",
    "admin.accounts.col.name": "Name in English",
    "admin.accounts.col.nameAr": "Name in Arabic",
    "admin.accounts.col.mobile": "Mobile",
    "admin.accounts.col.status": "Status",
    "admin.accounts.col.telegram": "Telegram",
    "admin.accounts.telegramNotLinkedShort": "Not linked",
    "admin.accounts.createTitle": "Create employee account",
    "admin.accounts.employeeId": "Employee ID",
    "admin.accounts.fullName": "Name in English",
    "admin.accounts.fullNameAr": "Name in Arabic",
    "admin.accounts.password": "Password",
    "admin.accounts.confirmPassword": "Confirm password",
    "admin.accounts.passwordsMatch": "Passwords match",
    "admin.accounts.passwordsDoNotMatch": "Passwords do not match",
    "admin.accounts.admin": "Admin",
    "admin.accounts.active": "Active",
    "admin.accounts.creating": "Creating...",
    "admin.accounts.createAction": "Create account",
    "admin.accounts.loading": "Loading accounts...",
    "admin.accounts.statusActive": "Active",
    "admin.accounts.statusDisabled": "Disabled",
    "admin.accounts.disable": "Disable",
    "admin.accounts.enable": "Enable",
    "admin.accounts.changePassword": "Change password",
    "admin.accounts.delete": "Delete",
    "admin.accounts.permissionsByPage": "Permissions by page",
    "admin.accounts.savePermissions": "Save permissions",
    "admin.accounts.mobile": "Mobile phone (same as Telegram registration)",
    "admin.accounts.mobileOptional": "Mobile (optional)",
    "admin.accounts.optional": "optional",
    "admin.accounts.mobilePlaceholder": "e.g. +9665XXXXXXXX or 05XXXXXXXX",
    "admin.accounts.mobileHint":
      "When the technician shares this number with the Telegram bot, notifications link automatically.",
    "admin.accounts.telegramLinked": "Telegram linked",
    "admin.accounts.telegramNotLinked": "Not linked yet — ask them to open the bot and share their phone once.",
    "admin.accounts.hrLookupAction": "Find",
    "admin.accounts.hrLookupLoading": "Finding…",
    "admin.accounts.hrLookupHint":
      "Enter an employee ID (e.g. 211002) and click Find. Name and mobile are loaded from Users (211002 - Name format) or HR. Fill manually if not found.",
    "admin.accounts.hrLookupFoundUsers": "Found in Users list — name and mobile filled.",
    "admin.accounts.hrLookupFoundHr": "Found in HR database — name and mobile filled.",
    "admin.accounts.hrLookupNotFound":
      "Not found in Users or HR — enter details manually.",
    "admin.accounts.hrLookupExists": "An account with this employee ID already exists.",
    "admin.accounts.hrLookupFailed": "Could not look up employee.",
    "admin.accounts.userPickerLabel": "Select from Users list",
    "admin.accounts.userPickerPlaceholder": "Search user…",
    "admin.accounts.userPickerHint":
      "Pick an employee from the dropdown to fill ID, name, and mobile. You can still edit fields below or enter them manually.",
    "admin.accounts.userPickerClear": "Change",
    "admin.accounts.userPickerLoading": "Searching…",
    "admin.accounts.userPickerEmpty": "No matching users.",
    "admin.accounts.userPickerNoMobile": "No mobile on file",
    "admin.accounts.userPickerHasAccount": "Account already exists",
    "admin.accounts.userPickerSyncPlaceholder": "Search to sync…",
    "admin.accounts.userPickerSyncAction": "Update name & mobile",
    "admin.accounts.syncNow": "Sync from Users",
    "admin.accounts.syncHint":
      "Update name and mobile from the Users list without deleting the account. Ticket assignments are kept.",
    "admin.accounts.syncConfirm": "Update this account with the following from Users list?",
    "admin.accounts.syncIdMismatch": "Selected user does not match this account employee ID.",
    "admin.accounts.syncIdMismatchShort": "Different employee ID",
    "admin.accounts.syncNotFound": "No Users list entry found for this employee ID.",
    "admin.accounts.syncFailed": "Could not sync from Users list.",
    "admin.accounts.deleteConfirm": "Delete this account?",
    "admin.accounts.newPasswordPrompt": "New password",
    "admin.accounts.passwordUpdated": "Password updated",
    "admin.accounts.error.generic": "Failed",
    "admin.accounts.error.passwordMismatch":
      "Password and confirm password do not match",
    "admin.accounts.error.createFailed": "Create failed",
    "admin.accounts.error.updateFailed": "Update failed",
    "admin.accounts.error.deleteFailed": "Delete failed",
    "admin.accounts.error.passwordUpdateFailed": "Password update failed",
    "admin.accounts.page.dashboard": "Dashboard",
    "admin.accounts.page.tickets": "Tickets",
    "admin.accounts.page.newTicket": "Create ticket",
    "admin.accounts.page.users": "Users",
    "admin.accounts.page.catalog": "Catalog",
    "admin.accounts.page.telegramSetup": "Telegram setup",
    "admin.accounts.page.adminAccounts": "Admin accounts",
    "admin.accounts.page.routineDashboard": "Routine dashboard",
    "admin.accounts.page.assets": "Assets",
    "admin.accounts.page.assetCategories": "Asset settings",
    "admin.accounts.page.checklistTemplates": "Checklist templates",
    "admin.accounts.page.inspectionSchedules": "Inspection schedules",
    "admin.accounts.page.inspections": "Inspections",
    "admin.accounts.page.inspectionReports": "Inspection reports",
    "admin.accounts.pageDesc.routineDashboard": "Routine KPI dashboard",
    "admin.accounts.pageDesc.assets": "Equipment registry",
    "admin.accounts.pageDesc.assetCategories": "Asset locations, statuses, and categories",
    "admin.accounts.pageDesc.checklistTemplates": "Versioned checklists",
    "admin.accounts.pageDesc.inspectionSchedules": "Recurrence schedules",
    "admin.accounts.pageDesc.inspections": "Inspection tasks and progress",
    "admin.accounts.pageDesc.inspectionReports": "History and reports",
    "admin.accounts.field.inspectionAssign": "Assign inspections",
    "admin.accounts.field.inspectionUpdateProgress": "Update inspection progress",
    "admin.accounts.field.inspectionUploadImages": "Upload inspection images",
    "admin.accounts.field.inspectionReview": "Review inspections",
    "admin.accounts.field.inspectionComplete": "Submit/complete inspections",
    "admin.accounts.field.inspectionClose": "Close inspections",
    "admin.accounts.field.inspectionCancel": "Cancel inspections",
    "admin.accounts.pageDesc.dashboard": "Home dashboard",
    "admin.accounts.pageDesc.tickets": "Ticket list and details",
    "admin.accounts.pageDesc.newTicket": "Create new ticket page",
    "admin.accounts.pageDesc.users": "Users management",
    "admin.accounts.pageDesc.catalog": "Catalog data management",
    "admin.accounts.pageDesc.telegramSetup": "Telegram bot setup screen",
    "admin.accounts.pageDesc.adminAccounts": "Accounts and permissions admin",
    "admin.accounts.field.userMobileEdit": "Edit user mobile",
    "admin.accounts.field.userEmailEdit": "Edit user email",
    "admin.accounts.field.userDelete": "Delete user",
    "admin.accounts.field.ticketStatusEdit": "Edit ticket status",
    "admin.accounts.field.ticketPriorityEdit": "Edit ticket priority",
    "admin.accounts.field.ticketAssignmentEdit": "Edit ticket assignment",
    "admin.accounts.field.ticketCommentEdit": "Edit/Delete ticket comments",
    "admin.accounts.field.ticketAttachmentDelete": "Delete ticket attachment",
    "admin.accounts.field.ticketDelete": "Delete tickets",

    // Auto assignment
    "admin.autoAssignment.title": "Auto assignment",
    "admin.autoAssignment.intro":
      "Choose companies, a maintenance type, and one or more employees. All selected employees can work matching open tickets in Telegram (Tickets assigned to me). New matching tickets notify the whole team; the first selected employee is the primary assignee.",
    "admin.autoAssignment.company": "Company",
    "admin.autoAssignment.allCompanies": "All companies",
    "admin.autoAssignment.chooseCompanies": "Choose companies",
    "admin.autoAssignment.companySummaryMore": "{selected} +{count} more",
    "admin.autoAssignment.chooseEmployees": "Choose employees",
    "admin.autoAssignment.employeeSummaryMore": "{selected} +{count} more",
    "admin.autoAssignment.employeeSelectedCount": "{count} employee(s) selected.",
    "admin.autoAssignment.employeesLabel": "Employees",
    "admin.autoAssignment.validationRequired":
      "Choose at least one company, maintenance type, and employee",
    "admin.autoAssignment.validationCompanies":
      "Choose at least one company or use all companies",
    "admin.autoAssignment.validationEmployees": "Select at least one employee.",
    "admin.autoAssignment.errorLoadFailed":
      "Failed to load auto assignment data",
    "admin.autoAssignment.errorSaveFailed": "Failed to save rule",
    "admin.autoAssignment.errorDeleteFailed": "Failed to delete rule",
    "admin.autoAssignment.deleteConfirm": "Delete this auto-assign rule?",
    "admin.autoAssignment.applyAllHint":
      "This rule will apply to every company.",
    "admin.autoAssignment.companySelectedCount": "{count} company selected.",
    "admin.autoAssignment.chooseMaintenanceType": "Choose maintenance type",
    "admin.autoAssignment.chooseEmployee": "Choose employee",
    "admin.autoAssignment.saving": "Saving...",
    "admin.autoAssignment.saveRule": "Save rule",
    "admin.autoAssignment.loading": "Loading auto assignment rules...",
    "admin.autoAssignment.empty": "No auto assignment rules yet.",
    "admin.autoAssignment.companiesLabel": "Companies",
    "admin.autoAssignment.delete": "Delete",
    "admin.autoAssignment.edit": "Edit",
    "admin.autoAssignment.editTitle": "Edit auto assignment",
    "admin.autoAssignment.cancel": "Cancel",
    "admin.autoAssignment.saveChanges": "Save changes",
  },
  ar: {
    // Nav
    "nav.dashboard": "الصفحة الرئيسية",
    "nav.tickets": "التذاكر",
    "nav.newTicket": "+تذكرة جديدة",
    "nav.users": "المستخدمون",
    "nav.catalog": "الفئات",
    "nav.admin": "الإدارة",
    "nav.logout": "تسجيل الخروج",
    "nav.language": "اللغة",
    "nav.routine": "الفحوصات",
    "nav.routineInspections": "عرض الفحص",
    "nav.assets": "الأصول",
    "nav.menu": "القائمة",
    "nav.openMenu": "فتح القائمة",
    "nav.closeMenu": "إغلاق القائمة",

    // Asset management
    "assets.title": "إدارة الأصول",
    "assets.subtitle": "تسجيل وصيانة المعدات المستخدمة في الفحوصات الدورية.",
    "assets.registry.title": "سجل الأصول",
    "assets.settings.title": "إعدادات الأصول",
    "assets.settings.subtitle": "إعداد المواقع والحالات والفئات المستخدمة عند تسجيل الأصول.",
    "assets.settings.tab.categories": "الفئات",
    "assets.settings.tab.locations": "المواقع",
    "assets.settings.tab.status": "الحالة",
    "assets.settings.addLocation": "إضافة موقع",
    "assets.settings.addStatus": "إضافة حالة",
    "assets.settings.locationName": "اسم الموقع",
    "assets.settings.statusName": "اسم الحالة",
    "assets.settings.operational": "يُحسب كأصل نشط / قيد الخدمة",
    "assets.settings.operationalYes": "قيد الخدمة",
    "assets.settings.operationalNo": "خارج الخدمة",
    "assets.settings.list.categories": "فئات الأصول",
    "assets.settings.list.locations": "مواقع الأصول",
    "assets.settings.list.status": "حالات الأصول",
    "assets.settings.needLocation": "أضف المواقع من إعدادات الأصول",
    "assets.categories.title": "فئات الأصول",
    "assets.categories.subtitle": "تجميع الأصول حسب النوع أو الموقع للجدولة والتقارير.",
    "assets.back": "العودة إلى سجل الأصول",
    "assets.manageSettingsHint": "إعداد الخيارات من",
    "assets.stats.total": "إجمالي الأصول",
    "assets.stats.active": "نشط",
    "assets.stats.inactive": "غير نشط",

    // Routine inspections
    "routine.dashboard.title": "الفحوصات الدورية",
    "routine.dashboard.subtitle": "تخطيط وجدولة ومتابعة فحوصات المعدات الدورية.",
    "routine.dashboard.dueSoon": "مستحقة قريباً (7 أيام)",
    "routine.dashboard.overdue": "متأخرة",
    "routine.dashboard.inProgress": "قيد التنفيذ",
    "routine.dashboard.completedMonth": "مكتملة هذا الشهر",
    "routine.assets.title": "الأصول",
    "routine.categories.title": "فئات الأصول",
    "routine.checklists.title": "قوالب قوائم الفحص",
    "routine.checklists.viewItems": "عرض البنود",
    "routine.checklists.editItems": "تعديل البنود",
    "routine.checklists.saveDraft": "حفظ المسودة",
    "routine.checklists.publish": "نشر",
    "routine.checklists.publishedSuccess": "تم نشر قائمة الفحص.",
    "routine.checklists.draftBadge": "مسودة",
    "routine.checklists.publishedBadge": "منشور",
    "routine.checklists.version": "الإصدار {n}",
    "routine.checklists.back": "العودة إلى القوالب",
    "routine.checklists.editingDraft": "تحتوي قائمة الفحص على مسودة غير منشورة.",
    "routine.checklists.publishedReadOnly": "قائمة فحص منشورة. انقر تعديل البنود لتغيير الخطوات.",
    "routine.schedules.title": "إضافة فحص جديد",
    "routine.schedules.subtitle": "حدد وتيرة فحص كل أصل والمسؤول عنه.",
    "routine.inspections.title": "عرض الفحص",
    "routine.reports.title": "تقارير الفحص",
    "routine.reports.subtitle": "عرض الفحوصات المكتملة بالتفاصيل الكاملة مع إمكانية الطباعة أو التصدير.",
    "routine.reports.detailTitle": "تقرير الفحص",
    "routine.reports.back": "العودة إلى التقارير",
    "routine.reports.print": "طباعة",
    "routine.reports.exportCsv": "تصدير CSV",
    "routine.reports.summary": "الملخص",
    "routine.reports.overallResult": "النتيجة الإجمالية",
    "routine.reports.overallNotes": "ملاحظات عامة",
    "routine.reports.building": "المبنى",
    "routine.reports.checklist": "قائمة الفحص",
    "routine.reports.scheduledAt": "مجدول",
    "routine.reports.startedAt": "بدء",
    "routine.reports.submittedAt": "إرسال",
    "routine.reports.completedAt": "اكتمال",
    "routine.reports.failedItems": "البنود الفاشلة",
    "routine.reports.correctiveTicket": "تذكرة تصحيحية",
    "routine.reports.checklistResults": "نتائج قائمة الفحص",
    "routine.reports.item": "البند",
    "routine.reports.response": "الإجابة",
    "routine.reports.notes": "ملاحظات",
    "routine.reports.result": "النتيجة",
    "routine.reports.passed": "ناجح",
    "routine.reports.failed": "فشل",
    "routine.reports.progressHistory": "سجل التقدم",
    "routine.reports.images": "الصور",
    "routine.empty": "لا توجد سجلات بعد.",
    "routine.view": "عرض",
    "routine.status": "الحالة",
    "routine.due": "الاستحقاق",
    "routine.nextDue": "الاستحقاق التالي",
    "routine.asset": "الأصل",
    "routine.back": "العودة إلى الفحوصات",
    "routine.create.new": "جديد",
    "routine.create.save": "حفظ",
    "routine.create.saving": "جارٍ الحفظ…",
    "routine.create.error": "تعذر الحفظ.",
    "routine.create.categoryTitle": "إضافة فئة",
    "routine.create.categoryName": "الاسم",
    "routine.create.categoryDesc": "الوصف",
    "routine.create.assetTitle": "إضافة أصل",
    "routine.create.assetCode": "رمز الأصل",
    "routine.create.assetName": "الاسم",
    "routine.create.assetCategory": "الفئة",
    "routine.create.assetLocation": "الموقع",
    "routine.create.needCategory": "أنشئ فئة أصول أولاً.",
    "routine.create.checklistTitle": "إضافة قالب فحص",
    "routine.create.checklistName": "اسم قائمة الفحص",
    "routine.create.checklistItems": "بنود قائمة الفحص",
    "routine.create.checklistItemsHint": "أضف كل خطوة يجب على الفني التحقق منها أثناء الفحص.",
    "routine.create.checklistItem": "عنوان البند",
    "routine.create.addItem": "إضافة بند",
    "routine.create.removeItem": "إزالة",
    "routine.create.itemsRequired": "أضف بندًا واحدًا على الأقل.",
    "routine.create.connectInspection": "ربط بفحص",
    "routine.create.linkAsset": "الأصل",
    "routine.create.linkAssetHint": "يمكنك ربط قائمة الفحص بأصل لجدولة فحوصات دورية. أو يمكن الربط لاحقًا من جداول الفحص.",
    "routine.create.noAssetLink": "بدون ربط الآن",
    "routine.create.checklistItemCount": "{count} بنود",
    "routine.create.defaultItem": "فحص عام",
    "routine.create.scheduleTitle": "إضافة جدول فحص",
    "routine.create.scheduleNeedPrereq": "أنشئ أصلًا واحدًا وقالب فحص منشورًا أولاً.",
    "routine.create.scheduleAsset": "الأصل",
    "routine.create.scheduleChecklist": "إصدار القالب",
    "routine.create.scheduleFrequency": "التكرار",
    "routine.create.scheduleStart": "تاريخ البدء",
    "routine.create.scheduleAssignee": "تعيين للفني",
    "routine.create.unassigned": "غير مُسند",
    "routine.create.pick": "اختر…",
    "routine.create.required": "يرجى تعبئة جميع الحقول المطلوبة.",
    "routine.create.generateHint": "بعد حفظ الجدول، أنشئ مهام الفحص للجداول المستحقة.",
    "routine.create.generateButton": "إنشاء الفحوصات المستحقة",
    "routine.create.generateRunning": "جارٍ الإنشاء…",
    "routine.create.generateSuccess": "تم إنشاء {count} مهمة فحص.",
    "routine.create.generateError": "فشل الإنشاء.",
    "routine.setup.title": "إعداد الفحوصات الدورية",
    "routine.setup.step1": "١. إضافة فئات الأصول",
    "routine.setup.step2": "٢. تسجيل الأصول",
    "routine.setup.step3": "٣. إنشاء قوالب الفحص",
    "routine.setup.step4": "٤. إعداد الجداول وإنشاء المهام",
    "routine.edit": "تعديل",
    "routine.delete": "حذف",
    "routine.cancel": "إلغاء الفحص",
    "routine.cancelConfirm": "إلغاء مهمة الفحص هذه؟",
    "routine.deleteConfirm": "حذف هذا العنصر؟",
    "routine.active": "نشط",
    "routine.inactive": "غير نشط",
    "routine.yes": "نعم",
    "routine.no": "لا",
    "routine.assign": "تعيين",
    "routine.assignee": "الفني",
    "routine.assignPick": "اختر الفني",
    "routine.confirmDelete": "نعم، احذف",
    "routine.cancelAction": "إلغاء",
    "routine.savedSuccess": "تم الحفظ بنجاح.",
    "routine.deletedSuccess": "تم الحذف بنجاح.",
    "routine.deactivatedSuccess": "تم التعطيل (يوجد فحوصات مرتبطة).",
    "routine.cancelledSuccess": "تم إلغاء مهمة الفحص.",
    "routine.confirmCancel": "نعم، إلغاء",

    // Users
    "users.title": "المستخدمون",
    "users.col.id": "المعرّف",
    "users.col.employeeId": "رقم الموظف",
    "users.col.employeeIdHint": "يُستخرج من الاسم عند استخدام الصيغة 211002 - الاسم",
    "users.col.name": "الاسم بالإنجليزي",
    "users.col.nameEn": "الاسم بالعربي",
    "users.col.mobile": "الجوال",
    "users.col.email": "البريد",
    "users.col.telegram": "تيليجرام",
    "users.col.created": "تاريخ الإنشاء",
    "users.empty": "لا يوجد مستخدمون بعد.",
    "users.searchPlaceholder": "ابحث بالاسم أو الجوال أو البريد…",
    "users.noResults": "لا يوجد مستخدمون مطابقون لبحثك.",
    "users.edit": "تعديل",
    "users.editTitle": "تعديل مستخدم",
    "users.save": "حفظ",
    "users.cancel": "إلغاء",
    "users.saving": "جارٍ الحفظ…",
    "users.errorSave": "تعذر حفظ التغييرات.",
    "users.addNew": "إضافة مستخدم",
    "users.addTitle": "إضافة مستخدم جديد",
    "users.errorCreate": "تعذر إنشاء المستخدم.",
    "users.needOneField":
      "أدخل قيمة واحدة على الأقل: رقم الموظف، الاسم (إنجليزي/عربي)، الجوال، أو البريد.",
    "users.employeeIdExists": "المستخدم موجود مسبقاً",
    "users.delete": "حذف",
    "users.deleteConfirm": "حذف هذا المستخدم؟ لا يمكن التراجع عن ذلك.",
    "users.deleting": "جارٍ الحذف…",
    "users.errorDelete": "تعذر حذف المستخدم.",
    "users.syncHr": "مزامنة من الموارد البشرية",
    "users.syncingHr": "جارٍ المزامنة من الموارد البشرية…",
    "users.syncHrDone": "اكتملت المزامنة: تمت إضافة {inserted} وتحديث {updated} (من أصل {fetched}).",
    "users.syncHrFailed": "تعذرت مزامنة المستخدمين من الموارد البشرية.",

    // Catalog setup
    "catalog.title": " ادارة الفئات",
    "catalog.company": "الشركة",
    "catalog.branch": "الفرع",
    "catalog.department": "القسم",
    "catalog.requestCategory": "تصنيف الطلب",
    "catalog.subcategory": "التصنيف الفرعي",
    "catalog.nameEn": "الاسم (إنجليزي)",
    "catalog.nameAr": "الاسم (عربي)",
    "catalog.add": "إضافة",
    "catalog.saving": "جارٍ الحفظ...",
    "catalog.deleting": "جارٍ الحذف...",
    "catalog.edit": "تعديل",
    "catalog.delete": "حذف",
    "catalog.search": "ابحث ...",
    "catalog.confirmDelete": "حذف هذا السجل؟",
    "catalog.empty": "لا توجد بيانات بعد.",
    "catalog.selectCompany": "اختر الشركة",
    "catalog.selectBranch": "اختر الفرع",
    "catalog.selectDepartment": "اختر القسم",
    "catalog.selectRequestCategory": "اختر تصنيف الطلب",

    // Dashboard
    "home.hero.pill": "سريع • منظم • متصل",
    "home.hero.title": "نظام التذاكر الالكتروني لشركة العامر- قسم الصيانة",
    "home.hero.subtitle":
      "أنشئ التذاكر وتابعها وأغلقها أول بأول .",
    "home.hero.ctaPrimary": "إنشاء تذكرة الآن",
    "home.hero.ctaSecondary": "عرض كل التذاكر",
    "home.actions.create.title": "إنشاء تذكرة",
    "home.actions.create.body":
      "سجّل طلبًا جديدًا خلال ثوانٍ وحافظ على ترتيب قائمة الدعم.",
    "home.actions.track.title": "متابعة التذاكر",
    "home.actions.track.body":
      "اعرف بسرعة ما هو مفتوح أو قيد العمل أو تم حله أو مغلق.",
    "home.actions.telegram.title": "بوت تيليجرام",
    "home.actions.telegram.body":
      "اسمح للمستخدمين بإنشاء التذاكر ومتابعتها من تيليجرام. دعم سريع من الجوال.",
    "home.tip.lead": "معلومة:",
    "home.tip.body":
      "اربط Webhook للبوت على `/api/telegram/webhook` وستتمكن من إنشاء وعرض التذاكر من داخل الدردشة.",

    "dashboard.title": "ملخص التذاكر",
    "dashboard.subtitle": "نظرة مباشرة على نشاط تذاكر الصيانة.",
    "dashboard.total": "إجمالي التذاكر",
    "dashboard.new": "جديدة",
    "dashboard.inProgress": "قيد التنفيذ",
    "dashboard.pending": "معلقة",
    "dashboard.closed": "مغلقة",
    "dashboard.reopened": "أعيد فتحها",
    "dashboard.chartStatus": "التذاكر حسب الحالة",
    "dashboard.chartTechnicians": "المُسندة لكل فني",
    "dashboard.chartTrend": "تذاكر جديدة (آخر ٧ أيام)",
    "dashboard.unassigned": "تذاكر غير مُسندة",
    "dashboard.noData": "لا توجد بيانات بعد.",
    "dashboard.status.OPEN": "جديدة / مفتوحة",
    "dashboard.status.NEW": "جديدة",
    "dashboard.status.ASSIGNED": "مُسندة",
    "dashboard.status.IN_PROGRESS": "قيد التنفيذ",
    "dashboard.status.PENDING": "معلقة",
    "dashboard.status.RESOLVED": "تم الحل",
    "dashboard.status.CLOSED": "مغلقة",
    "dashboard.status.REOPENED": "أعيد فتحها",
    "dashboard.filterAssignee": "المُسند إليه",
    "dashboard.filterStatus": "حالة التذكرة",
    "dashboard.allAssignees": "كل المُسندين",
    "dashboard.allStatuses": "كل الحالات",
    "dashboard.ticketsLabel": "تذكرة",
    "dashboard.showing": "عرض",
    "dashboard.noFilterResults": "لا توجد تذاكر تطابق هذه الفلاتر.",

    // Telegram setup
    "telegramSetup.title": "ربط بوت تيليجرام",
    "telegramSetup.intro":
      "اتبع الخطوات التالية ليتمكن المستخدمون من إنشاء التذاكر ومتابعتها من تيليجرام.",
    "telegramSetup.warning":
      "تعرض هذه الصفحة رمز البوت. أي شخص يصل إلى هذا الرابط قد يراه. أضف تسجيل دخول أو استخدم شبكة موثوقة قبل نشر التطبيق على الإنترنت.",
    "telegramSetup.step1Title": "١. إنشاء بوت عبر BotFather",
    "telegramSetup.step1Body":
      "في تيليجرام افتح @BotFather وأرسل /newbot واختر الاسم واسم المستخدم ثم انسخ رمز HTTP API.",
    "telegramSetup.step2Title": "٢. حفظ الرمز في متغيرات البيئة",
    "telegramSetup.step2Body":
      "في مجلد web افتح .env واضبط TELEGRAM_BOT_TOKEN على الرمز (بدون علامات اقتباس) ثم أعد تشغيل الخادم.",
    "telegramSetup.step3Title": "٣. استخدام HTTPS لعنوان الـ webhook",
    "telegramSetup.step3Body":
      "تيليجرام يتطلب عنوان https:// للـ webhook في الإنتاج. للتطوير المحلي استخدم نفقًا مثل ngrok واستخدم عنوان https العام.",
    "telegramSetup.step4Title": "٤. تسجيل الـ webhook",
    "telegramSetup.step4Body":
      "نفّذ setWebhook مرة واحدة باستخدام العنوان العام للتطبيق مع المسار أدناه. يمكنك استخدام curl أو فتح الرابط في المتصفح.",
    "telegramSetup.webhookLabel": "نقطة webhook (هذا التطبيق)",
    "telegramSetup.exampleCurl": "مثال curl",
    "telegramSetup.tokenSection": "رمز البوت الحالي (من الخادم)",
    "telegramSetup.tokenMissing":
      "لم يُضبط رمز. اضبط TELEGRAM_BOT_TOKEN في web/.env وأعد تشغيل الخادم.",
    "telegramSetup.tokenPlaceholder":
      "قيمة وهمية في .env. استبدل TELEGRAM_BOT_TOKEN بالرمز الحقيقي من BotFather.",
    "telegramSetup.copy": "نسخ",
    "telegramSetup.copied": "تم النسخ!",

    // Telegram bot (FSM)
    "bot.lang.title": "اختر لغتك",
    "bot.phone.request": "شارك رقم هاتفك للمتابعة.",
    "bot.phone.button": "مشاركة الهاتف",
    "bot.phone.saved": "تم حفظ الهاتف!",
    "bot.phone.notRegistered": "رقمك غير مسجل. يرجى التواصل مع المسؤول.",
    "bot.phone.mismatch":
      "هذا الرقم لا يطابق ملفك المسجل. يرجى مشاركة رقمك الشخصي المسجل.",
    "bot.menu.title": "القائمة الرئيسية",
    "bot.menu.create": "إنشاء تذكرة جديدة",
    "bot.menu.view": "عرض تذاكري",
    "bot.menu.changeDepartment": "تغيير القسم",
    "bot.menu.changeLang": "تغيير اللغة",
    "bot.menu.routineInspections": "الفحوصات الدورية",
    "bot.menu.assigned": "التذاكر المسندة إليّ",
    "bot.assigned.title": "التذاكر المسندة إليك",
    "bot.assigned.empty": "لا توجد تذاكر مسندة إليك حالياً.",
    "bot.assigned.details": "تذكرة مسندة",
    "bot.assigned.updateStatus": "تحديث الحالة",
    "bot.assigned.askImages":
      "التذكرة #{id}: أرسل صوراً (اختياري، حتى 5) ثم اضغط التالي — أو تخطي.",
    "bot.assigned.askCommentStep":
      "أضف تعليقاً (اختياري) ثم اضغط التالي — أو تخطي.",
    "bot.assigned.askStatus": "اختر الحالة الجديدة، أو إلغاء للعودة للقائمة الرئيسية.",
    "bot.assigned.next": "التالي",
    "bot.assigned.skip": "تخطي",
    "bot.assigned.cancel": "إلغاء",
    "bot.assigned.cancelled": "تم إلغاء التحديث. العودة للقائمة الرئيسية.",
    "bot.assigned.imageAdded":
      "تمت إضافة الصورة ({count}/{max}). أرسل المزيد أو اضغط التالي / تخطي.",
    "bot.assigned.imagesFull": "وصلت إلى الحد الأقصى 5 صور. اضغط التالي للمتابعة.",
    "bot.assigned.imageOnly": "يُسمح بالصور فقط في هذه الخطوة.",
    "bot.assigned.commentSaved": "تم حفظ التعليق. الآن اختر الحالة.",
    "bot.assigned.defaultComment": "تحديث",
    "bot.assigned.statusUpdated": "تم تحديث الحالة إلى: {status}",
    "bot.assigned.updatedDone": "تم تحديث التذكرة #{id}.",
    "bot.assigned.notYours": "هذه التذكرة غير مسندة إليك.",
    "bot.assigned.notEmployee": "هذا الخيار متاح للموظفين فقط.",
    "bot.assigned.closeAskResolution": "أرسل تفاصيل الحل لإغلاق التذكرة #{id}.",
    "bot.assigned.closeCancelled": "تم إلغاء الإغلاق.",
    "bot.assigned.back": "رجوع للتذاكر المسندة",
    "bot.assigned.cancelClose": "إلغاء",
    "bot.routine.backMenu": "القائمة الرئيسية",
    "bot.routine.backList": "العودة للقائمة",
    "bot.routine.updateProgress": "تحديث التقدم",
    "bot.routine.submitForReview": "إرسال للمراجعة",
    "bot.routine.submitted": "تم الإرسال للمراجعة.",
    "bot.routine.progressLocked": "التقدم 100%. أرسل للمراجعة — لا يمكن تغيير التقدم بعد الآن.",
    "bot.routine.pctTooLow": "لا يمكن تعيين نسبة أقل من نسبة التقدم الحالية.",
    "bot.routine.noHigherPct": "لا توجد نسب أعلى. استخدم مخصص، أو اختر حالة مكتمل.",
    "bot.routine.updateStatus": "تحديث الحالة",
    "bot.routine.updatePct": "تحديث نسبة الإنجاز",
    "bot.routine.addNote": "إضافة ملاحظة",
    "bot.routine.uploadImage": "رفع صور",
    "bot.routine.pickStatus": "الخطوة 1/3 — اختر حالة التقدم:",
    "bot.routine.pickPct": "الخطوة 2/3 — اختر نسبة الإنجاز:",
    "bot.routine.enterProgressNote": "الخطوة 3/3 — أرسل ملاحظة/تعليق لهذا التحديث:",
    "bot.routine.enterCustomPct": "أدخل نسبة أعلى من الحالية (حد أقصى 99):",
    "bot.routine.invalidCustomPct": "نسبة غير صالحة (يجب أن تكون أعلى من الحالية وأقل من 100).",
    "bot.routine.sendPhotoOrSkip": "يمكنك إرسال صورة لهذا التحديث، أو اضغط تخطي.",
    "bot.routine.skipPhoto": "تخطي الصورة",
    "bot.routine.noteRequired": "يرجى إرسال ملاحظة (رسالة نصية).",
    "bot.routine.enterNote": "أرسل ملاحظتك:",
    "bot.routine.sendPhoto": "أرسل صورة الآن.",
    "bot.routine.saved": "تم الحفظ.",
    "bot.routine.imageSaved": "تم حفظ الصورة.",
    "routine.activity.title": "النشاط",
    "routine.activity.empty": "لا توجد تحديثات تقدم بعد.",
    "routine.activity.olderImages": "صور أقدم",
    "routine.activity.status": "الحالة",
    "routine.activity.percent": "الإنجاز",
    "routine.activity.note": "ملاحظة",
    "bot.create.ask.skip": "تخطي",
    "bot.create.ask.enter": "أدخل الاسم",
    "bot.create.enterPrompt": "أرسل الاسم (نص).",
    "bot.create.descriptionPrompt": "أرسل وصفًا مختصرًا (حد أدنى 3 أحرف).",
    "bot.create.descriptionInvalid": "الوصف قصير جدًا. يرجى كتابة 3 أحرف على الأقل.",
    "bot.create.attachQuestion": "هل ترغب بإرفاق ملف؟",
    "bot.create.attachYes": "نعم، إرفاق ملف",
    "bot.create.attachNo": "بدون مرفق",
    "bot.create.attachmentInstruction": "أرسل صورة أو مستندًا (الحد الأقصى 10 ميجابايت).",
    "bot.create.attachmentInvalid": "مرفق غير صالح. أرسل صورة أو مستندًا.",
    "bot.create.attachmentTooLarge": "حجم المرفق كبير جدًا. الحد الأقصى 10 ميجابايت.",
    "bot.create.confirmTitle": "تأكيد التذكرة",
    "bot.create.confirmSubject": "الموضوع",
    "bot.create.confirmDescription": "الوصف",
    "bot.create.confirmPhone": "الهاتف",
    "bot.create.confirmDetails": "التفاصيل",
    "bot.create.confirmAttachment": "المرفق",
    "bot.create.confirmAttachmentNone": "لا يوجد",
    "bot.create.confirmYes": "تأكيد ✅",
    "bot.create.confirmEdit": "تعديل ✏️",
    "bot.create.confirmCancel": "إلغاء ❌",
    "bot.create.confirmInProgress": "يرجى الانتظار… نحن نقوم بإنشاء تذكرتك بالفعل.",
    "bot.create.cancelled": "تم الإلغاء. العودة إلى القائمة الرئيسية.",
    "bot.create.sessionExpired":
      "انتهت مهلة الجلسة. تم إعادة تعيين مسودة التذكرة. الرجاء البدء من القائمة الرئيسية.",
    "bot.create.created": "تم إنشاء التذكرة بنجاح!",
    "bot.create.ticketId": "رقم التذكرة: ",
    "bot.create.attachmentFailed": "تم إنشاء التذكرة، لكن فشل رفع المرفق.",
    "bot.view.title": "تذاكرك",
    "bot.view.empty": "لا توجد تذاكر لديك بعد.",
    "bot.view.back": "رجوع",
    "bot.view.details": "تفاصيل التذكرة",
    "bot.view.detailsAttachment": "المرفق",
    "bot.view.noComments": "لا توجد تعليقات بعد.",
    "bot.error.general": "حدث خطأ ما. حاول مرة أخرى.",
    "bot.contact.invalid": "بيانات تواصل غير صالحة. يرجى استخدام زر مشاركة الهاتف.",
    "bot.create.step.company": "اختر الشركة",
    "bot.create.step.branch": "اختر الفرع",
    "bot.create.step.site": "اختر الموقع / الفرع",
    "bot.create.step.department": "اختر القسم",
    "bot.create.step.category": "اختر التصنيف",
    "bot.create.step.subcategory": "اختر التصنيف الفرعي",
    "bot.create.step.item": "اختر العنصر",

    // Tickets
    "tickets.new.title": "تذكرة جديدة",
    "tickets.field.title": "العنوان",
    "tickets.field.description": "الوصف",
    "tickets.field.priority": "الأولوية",
    "tickets.field.company": "الشركة",
    "tickets.field.companyPlaceholder": "اختر الشركة",
    "tickets.field.category": "التصنيف",
    "tickets.field.categoryPlaceholder": "اختر التصنيف",
    "tickets.field.assignedTo": "تعيين إلى",
    "tickets.field.assignedToNone": "غير معيّن",
    "tickets.field.attachment": "مرفق (صورة)",
    "tickets.field.attachmentHint":
      "اختياري. JPEG أو PNG أو WebP أو GIF. بحد أقصى 5 ميجابايت.",
    "tickets.error.invalidImage": "اختر ملف صورة صالحًا.",
    "tickets.error.fileTooLarge": "يجب ألا يتجاوز حجم الصورة 5 ميجابايت.",
    "tickets.error.dbColumn":
      "قاعدة البيانات التي يتصل بها التطبيق (من DATABASE_URL في .env: نفس الخادم واسم database=) لا تزال بدون عمود attachmentUrl، أو يجب إعادة تشغيل الخادم. اتصل من SSMS بنفس الخادم وقاعدة البيانات ثم نفّذ ALTER TABLE. أوقف npm run dev ثم npx prisma generate ثم شغّل من جديد.",
    "tickets.action.create": "إنشاء تذكرة",
    "tickets.action.creating": "جارٍ الإنشاء...",
    "tickets.error.companyRequired": "يرجى اختيار الشركة.",
    "tickets.error.categoryRequired": "يرجى اختيار التصنيف.",
    "tickets.error.createFailed": "فشل إنشاء التذكرة",
    "tickets.list.title": "التذاكر",
    "tickets.list.new": "تذكرة جديدة",
    "tickets.list.empty": "لا توجد تذاكر بعد.",
    "tickets.list.emptyFiltered": "لا توجد تذاكر تطابق هذه الفلاتر.",
    "tickets.list.createFirst": "أنشئ الأولى",
    "tickets.list.filters": "فلاتر التذاكر",
    "tickets.list.filter.search": "بحث",
    "tickets.list.filter.searchPlaceholder": "العنوان أو رقم التذكرة",
    "tickets.list.filter.status": "الحالة",
    "tickets.list.filter.priority": "الأولوية",
    "tickets.list.filter.owner": "المسؤول",
    "tickets.list.filter.allStatuses": "كل الحالات",
    "tickets.list.filter.allPriorities": "كل الأولويات",
    "tickets.list.filter.allOwners": "كل المسؤولين",
    "tickets.list.filter.unassigned": "غير معيّن",
    "tickets.list.filter.apply": "تطبيق",
    "tickets.list.filter.clear": "مسح",
    "tickets.list.showing": "عرض {count} {label}",
    "tickets.list.ticketsLabel": "تذكرة",
    "tickets.list.col.id": "المعرف",
    "tickets.list.col.title": "العنوان",
    "tickets.list.col.status": "الحالة",
    "tickets.list.col.priority": "الأولوية",
    "tickets.list.col.owner": "المسؤول",
    "tickets.list.col.requestCategory": "التصنيف",
    "tickets.list.col.created": "تاريخ الإنشاء",
    "tickets.list.page": "صفحة {page} من {total}",
    "tickets.list.prev": "السابق",
    "tickets.list.next": "التالي",

    "login.brandCompany": "شركة عبدالله علي العامر وإخوانه",
    "login.brandTagline": "نظام تذاكر الصيانة",
    "login.title": "تسجيل الدخول",
    "login.bootstrapTitle": "إنشاء المسؤول الأول",
    "login.hint": "استخدم رقم الموظف وكلمة المرور.",
    "login.employeeId": "رقم الموظف",
    "login.password": "كلمة المرور",
    "login.fullName": "الاسم الكامل",
    "login.submit": "دخول",
    "login.bootstrapSubmit": "إنشاء المسؤول",
    "login.loading": "يرجى الانتظار…",

    // Ticket detail labels
    "ticket.status": "الحالة",
    "ticket.priority": "الأولوية",
    "ticket.priority.LOW": "منخفضة",
    "ticket.priority.MEDIUM": "متوسطة",
    "ticket.priority.HIGH": "عالية",
    "ticket.priority.URGENT": "عاجلة",
    "ticket.resolutionDetails": "تفاصيل الحل (مطلوبة للإغلاق)",
    "ticket.resolutionPlaceholder": "صف ما تم فعله لحل المشكلة…",
    "ticket.closeTicket": "إغلاق التذكرة",
    "ticket.cancel": "إلغاء",
    "ticket.owner": "المالك",
    "ticket.ownerPhone": "هاتف المالك",
    "ticket.assignedTo": "معيّن إلى",
    "ticket.assignedPeople": "الموظفون المعيّنون",
    "ticket.assignedPeopleNone": "لا يوجد موظفون معيّنون تلقائياً لهذا التصنيف",
    "ticket.unassigned": "غير معيّن",
    "ticket.back": "العودة إلى التذاكر",
    "ticket.details": "التفاصيل",
    "ticket.attachment": "المرفق",
    "ticket.downloadAttachment": "تنزيل المرفق",
    "ticket.delete": "حذف التذكرة",
    "ticket.deleting": "جارٍ الحذف…",
    "ticket.deleteConfirm": "حذف التذكرة #{id}؟ لا يمكن التراجع عن ذلك.",
    "ticket.deleteError": "تعذر حذف التذكرة.",
    "ticket.resolution": "الحل",
    "ticket.closed": "مغلق",
    "ticket.company": "الشركة",
    "ticket.branch": "الفرع",
    "ticket.department": "القسم",
    "ticket.requestCategory": "تصنيف الطلب",
    "ticket.subcategory": "التصنيف الفرعي",
    "ticket.created": "تاريخ الإنشاء",
    "ticket.updated": "آخر تحديث",
    "ticket.comments.title": "التعليقات",
    "ticket.comments.empty": "لا توجد تعليقات بعد.",
    "ticket.comments.author": "اسمك (اختياري)",
    "ticket.comments.placeholder": "اكتب ملاحظة أو تحديثًا داخليًا للفريق…",
    "ticket.comments.post": "إضافة تعليق",
    "ticket.comments.posting": "جارٍ الإرسال…",
    "ticket.comments.error": "تعذر نشر التعليق.",
    "ticket.comments.edit": "تعديل",
    "ticket.comments.delete": "حذف",
    "ticket.comments.save": "حفظ",
    "ticket.comments.cancel": "إلغاء",
    "ticket.comments.confirmDelete": "حذف هذا التعليق؟",
    "ticket.comments.updateError": "تعذر تحديث التعليق.",
    "ticket.comments.deleteError": "تعذر حذف التعليق.",
    "ticket.comments.attachImages": "إرفاق صور (اختياري)",
    "ticket.comments.attachHint": "صور فقط · حتى 5 · بحد أقصى 5 ميجابايت لكل صورة",
    "ticket.comments.attachTooMany": "يمكنك إرفاق 5 صور كحد أقصى.",
    "ticket.comments.attachInvalid": "يُسمح بملفات الصور فقط.",
    "ticket.comments.removeImage": "إزالة الصورة",

    // Admin accounts
    "admin.accounts.pageTitle": "حسابات الموظفين",
    "admin.accounts.detailTitle": "حساب الموظف",
    "admin.accounts.backToList": "العودة إلى الموظفين",
    "admin.accounts.searchPlaceholder": "ابحث بالاسم أو رقم الموظف أو الجوال…",
    "admin.accounts.addNew": "إضافة موظف",
    "admin.accounts.cancel": "إلغاء",
    "admin.accounts.empty": "لا توجد حسابات موظفين بعد.",
    "admin.accounts.noResults": "لا يوجد موظفون مطابقون لبحثك.",
    "admin.accounts.col.id": "المعرف",
    "admin.accounts.col.employeeId": "رقم الموظف",
    "admin.accounts.col.name": "الاسم بالإنجليزي",
    "admin.accounts.col.nameAr": "الاسم بالعربي",
    "admin.accounts.col.mobile": "الجوال",
    "admin.accounts.col.status": "الحالة",
    "admin.accounts.col.telegram": "تيليجرام",
    "admin.accounts.telegramNotLinkedShort": "غير مربوط",
    "admin.accounts.createTitle": "إنشاء حساب موظف",
    "admin.accounts.employeeId": "رقم الموظف",
    "admin.accounts.fullName": "الاسم بالإنجليزي",
    "admin.accounts.fullNameAr": "الاسم بالعربي",
    "admin.accounts.password": "كلمة المرور",
    "admin.accounts.confirmPassword": "تأكيد كلمة المرور",
    "admin.accounts.passwordsMatch": "كلمتا المرور متطابقتان",
    "admin.accounts.passwordsDoNotMatch": "كلمتا المرور غير متطابقتين",
    "admin.accounts.admin": "مسؤول",
    "admin.accounts.active": "نشط",
    "admin.accounts.creating": "جارٍ الإنشاء...",
    "admin.accounts.createAction": "إنشاء الحساب",
    "admin.accounts.loading": "جارٍ تحميل الحسابات...",
    "admin.accounts.statusActive": "نشط",
    "admin.accounts.statusDisabled": "معطل",
    "admin.accounts.disable": "تعطيل",
    "admin.accounts.enable": "تفعيل",
    "admin.accounts.changePassword": "تغيير كلمة المرور",
    "admin.accounts.delete": "حذف",
    "admin.accounts.permissionsByPage": "الصلاحيات حسب الصفحة",
    "admin.accounts.savePermissions": "حفظ الصلاحيات",
    "admin.accounts.mobile": "رقم الجوال (نفس تسجيل تيليجرام)",
    "admin.accounts.mobileOptional": "الجوال (اختياري)",
    "admin.accounts.optional": "اختياري",
    "admin.accounts.mobilePlaceholder": "مثال: +9665XXXXXXXX أو 05XXXXXXXX",
    "admin.accounts.mobileHint":
      "عند مشاركة الفني لهذا الرقم مع بوت تيليجرام، تُربط الإشعارات تلقائياً.",
    "admin.accounts.telegramLinked": "تيليجرام مربوط",
    "admin.accounts.telegramNotLinked": "غير مربوط بعد — اطلب من الفني فتح البوت ومشاركة رقم الهاتف مرة واحدة.",
    "admin.accounts.hrLookupAction": "بحث",
    "admin.accounts.hrLookupLoading": "جارٍ البحث…",
    "admin.accounts.hrLookupHint":
      "أدخل رقم الموظف (مثل 211002) واضغط بحث. يُحمّل الاسم والجوال من المستخدمين (صيغة 211002 - الاسم) أو HR.",
    "admin.accounts.hrLookupFoundUsers": "وُجد في قائمة المستخدمين — تم تعبئة الاسم والجوال.",
    "admin.accounts.hrLookupFoundHr": "وُجد في قاعدة HR — تم تعبئة الاسم والجوال.",
    "admin.accounts.hrLookupNotFound":
      "غير موجود في المستخدمين أو HR — أدخل البيانات يدوياً.",
    "admin.accounts.hrLookupExists": "يوجد حساب بهذا الرقم بالفعل.",
    "admin.accounts.hrLookupFailed": "تعذر البحث عن الموظف.",
    "admin.accounts.userPickerLabel": "اختر من قائمة المستخدمين",
    "admin.accounts.userPickerPlaceholder": "بحث عن مستخدم…",
    "admin.accounts.userPickerHint":
      "اختر موظفاً من القائمة لتعبئة الرقم والاسم والجوال. يمكنك تعديل الحقول أدناه أو إدخالها يدوياً.",
    "admin.accounts.userPickerClear": "تغيير",
    "admin.accounts.userPickerLoading": "جارٍ البحث…",
    "admin.accounts.userPickerEmpty": "لا يوجد مستخدمون مطابقون.",
    "admin.accounts.userPickerNoMobile": "لا يوجد جوال مسجل",
    "admin.accounts.userPickerHasAccount": "الحساب موجود مسبقاً",
    "admin.accounts.userPickerSyncPlaceholder": "بحث للمزامنة…",
    "admin.accounts.userPickerSyncAction": "تحديث الاسم والجوال",
    "admin.accounts.syncNow": "مزامنة من المستخدمين",
    "admin.accounts.syncHint":
      "حدّث الاسم والجوال من قائمة المستخدمين دون حذف الحساب. تبقى التذاكر والمهام المُسندة كما هي.",
    "admin.accounts.syncConfirm": "تحديث هذا الحساب بالبيانات التالية من قائمة المستخدمين؟",
    "admin.accounts.syncIdMismatch": "المستخدم المختار لا يطابق رقم موظف هذا الحساب.",
    "admin.accounts.syncIdMismatchShort": "رقم موظف مختلف",
    "admin.accounts.syncNotFound": "لا يوجد سجل في قائمة المستخدمين لهذا الرقم.",
    "admin.accounts.syncFailed": "تعذت المزامنة من قائمة المستخدمين.",
    "admin.accounts.deleteConfirm": "حذف هذا الحساب؟",
    "admin.accounts.newPasswordPrompt": "كلمة المرور الجديدة",
    "admin.accounts.passwordUpdated": "تم تحديث كلمة المرور",
    "admin.accounts.error.generic": "فشلت العملية",
    "admin.accounts.error.passwordMismatch":
      "كلمة المرور وتأكيد كلمة المرور غير متطابقين",
    "admin.accounts.error.createFailed": "فشل إنشاء الحساب",
    "admin.accounts.error.updateFailed": "فشل التحديث",
    "admin.accounts.error.deleteFailed": "فشل الحذف",
    "admin.accounts.error.passwordUpdateFailed": "فشل تحديث كلمة المرور",
    "admin.accounts.page.dashboard": "الصفحة الرئيسية",
    "admin.accounts.page.tickets": "التذاكر",
    "admin.accounts.page.newTicket": "إنشاء تذكرة",
    "admin.accounts.page.users": "المستخدمون",
    "admin.accounts.page.catalog": "الفئات",
    "admin.accounts.page.telegramSetup": "إعداد تيليجرام",
    "admin.accounts.page.adminAccounts": "حسابات الإدارة",
    "admin.accounts.page.routineDashboard": "لوحة الفحوصات",
    "admin.accounts.page.assets": "الأصول",
    "admin.accounts.page.assetCategories": "إعدادات الأصول",
    "admin.accounts.page.checklistTemplates": "قوالب الفحص",
    "admin.accounts.page.inspectionSchedules": "جداول الفحص",
    "admin.accounts.page.inspections": "مهام الفحص",
    "admin.accounts.page.inspectionReports": "تقارير الفحص",
    "admin.accounts.pageDesc.routineDashboard": "مؤشرات الفحوصات الدورية",
    "admin.accounts.pageDesc.assets": "سجل المعدات",
    "admin.accounts.pageDesc.assetCategories": "مواقع وحالات وفئات الأصول",
    "admin.accounts.pageDesc.checklistTemplates": "قوائم فحص بإصدارات",
    "admin.accounts.pageDesc.inspectionSchedules": "قواعد التكرار",
    "admin.accounts.pageDesc.inspections": "مهام الفحص والتقدم",
    "admin.accounts.pageDesc.inspectionReports": "السجل والتقارير",
    "admin.accounts.field.inspectionAssign": "تعيين الفحوصات",
    "admin.accounts.field.inspectionUpdateProgress": "تحديث تقدم الفحص",
    "admin.accounts.field.inspectionUploadImages": "رفع صور الفحص",
    "admin.accounts.field.inspectionReview": "مراجعة الفحوصات",
    "admin.accounts.field.inspectionComplete": "إرسال/إكمال الفحوصات",
    "admin.accounts.field.inspectionClose": "إغلاق الفحوصات",
    "admin.accounts.field.inspectionCancel": "إلغاء الفحوصات",
    "admin.accounts.pageDesc.dashboard": "لوحة الصفحة الرئيسية",
    "admin.accounts.pageDesc.tickets": "قائمة التذاكر وتفاصيلها",
    "admin.accounts.pageDesc.newTicket": "صفحة إنشاء تذكرة جديدة",
    "admin.accounts.pageDesc.users": "إدارة المستخدمين",
    "admin.accounts.pageDesc.catalog": "إدارة بيانات الفئات",
    "admin.accounts.pageDesc.telegramSetup": "شاشة إعداد بوت تيليجرام",
    "admin.accounts.pageDesc.adminAccounts": "إدارة الحسابات والصلاحيات",
    "admin.accounts.field.userMobileEdit": "تعديل جوال المستخدم",
    "admin.accounts.field.userEmailEdit": "تعديل بريد المستخدم",
    "admin.accounts.field.userDelete": "حذف المستخدم",
    "admin.accounts.field.ticketStatusEdit": "تعديل حالة التذكرة",
    "admin.accounts.field.ticketPriorityEdit": "تعديل أولوية التذكرة",
    "admin.accounts.field.ticketAssignmentEdit": "تعديل تعيين التذكرة",
    "admin.accounts.field.ticketCommentEdit": "تعديل/حذف تعليقات التذاكر",
    "admin.accounts.field.ticketAttachmentDelete": "حذف مرفق التذكرة",
    "admin.accounts.field.ticketDelete": "حذف التذاكر",

    // Auto assignment
    "admin.autoAssignment.title": "التعيين التلقائي",
    "admin.autoAssignment.intro":
      "اختر الشركات ونوع الصيانة وموظفًا واحدًا أو أكثر. يمكن لجميع الموظفين المختارين العمل على التذاكر المفتوحة المطابقة من تيليجرام (التذاكر المسندة إليّ). التذاكر الجديدة تُشعر الفريق كاملًا، ويُعيَّن أول موظف كمسؤول أساسي.",
    "admin.autoAssignment.company": "شركة",
    "admin.autoAssignment.allCompanies": "كل الشركات",
    "admin.autoAssignment.chooseCompanies": "اختر الشركات",
    "admin.autoAssignment.companySummaryMore": "{selected} +{count} المزيد",
    "admin.autoAssignment.chooseEmployees": "اختر الموظفين",
    "admin.autoAssignment.employeeSummaryMore": "{selected} +{count} المزيد",
    "admin.autoAssignment.employeeSelectedCount": "تم اختيار {count} موظف.",
    "admin.autoAssignment.employeesLabel": "الموظفون",
    "admin.autoAssignment.validationRequired":
      "اختر شركة واحدة على الأقل ونوع الصيانة والموظف",
    "admin.autoAssignment.validationCompanies":
      "اختر شركة واحدة على الأقل أو فعّل كل الشركات",
    "admin.autoAssignment.validationEmployees": "اختر موظفًا واحدًا على الأقل.",
    "admin.autoAssignment.errorLoadFailed":
      "تعذر تحميل بيانات التعيين التلقائي",
    "admin.autoAssignment.errorSaveFailed": "تعذر حفظ",
    "admin.autoAssignment.errorDeleteFailed": "تعذر حذف القاعدة",
    "admin.autoAssignment.deleteConfirm": "حذف قاعدة التعيين التلقائي هذه؟",
    "admin.autoAssignment.applyAllHint": "سيتم تطبيق هذه القاعدة على كل الشركات.",
    "admin.autoAssignment.companySelectedCount": "تم اختيار {count} شركة.",
    "admin.autoAssignment.chooseMaintenanceType": "اختر نوع الصيانة",
    "admin.autoAssignment.chooseEmployee": "اختر الموظف",
    "admin.autoAssignment.saving": "جارٍ الحفظ...",
    "admin.autoAssignment.saveRule": "حفظ",
    "admin.autoAssignment.loading": "جارٍ تحميل قواعد التعيين التلقائي...",
    "admin.autoAssignment.empty": "لا توجد قواعد تعيين تلقائي بعد.",
    "admin.autoAssignment.companiesLabel": "الشركات",
    "admin.autoAssignment.delete": "حذف",
    "admin.autoAssignment.edit": "تعديل",
    "admin.autoAssignment.editTitle": "تعديل التعيين التلقائي",
    "admin.autoAssignment.cancel": "إلغاء",
    "admin.autoAssignment.saveChanges": "حفظ التغييرات",
  },
};

export function t(locale: Locale, key: string) {
  return MESSAGES[locale][key] ?? MESSAGES.en[key] ?? key;
}